import { useState, useEffect } from "react"
import MainThemeLayout from "../../layout/MainThemeLayout"
import { getOrDeleteRequest, postOrPutRequest } from "../../utils/utils"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
} from "recharts"
import SelectMultiple from "../SelectMultiple"

const CompareProyects = () => {
  const isValidValue = (value) => {
    return value !== null && value !== undefined && value !== "null" && value !== "" && value !== 0 && !isNaN(value)
  }

  const [projects, setProjects] = useState([])
  const [selectedProjects, setSelectedProjects] = useState([])
  const [selectedPeriod, setSelectedPeriod] = useState("")
  const [periodType, setPeriodType] = useState("specific")
  const [startPeriod, setStartPeriod] = useState("")
  const [endPeriod, setEndPeriod] = useState("")
  const [commonIndicators, setCommonIndicators] = useState([])
  const [chartData, setChartData] = useState([])
  const [loading, setLoading] = useState(true)
  const [mockData, setMockData] = useState({})
  const [availablePeriods, setAvailablePeriods] = useState([])
  const [activeTab, setActiveTab] = useState("comparison")
  const [selectedProjectForDetail, setSelectedProjectForDetail] = useState("")
  const [selectedIndicatorForHistory, setSelectedIndicatorForHistory] = useState("")
  const [historyChartData, setHistoryChartData] = useState([])
  const [selectedIndicatorTerms, setSelectedIndicatorTerms] = useState([])
  const [companies, setCompanies] = useState([])
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [validationError, setValidationError] = useState("")
  const [selectedIndicatorForTemporal, setSelectedIndicatorForTemporal] = useState("")

  const getProjects = async (companyId = null) => {
    let url = `projects/filter`

    if (companyId) {
      url = `projects/projectbycompany/${companyId}`
    }

    const response = await getOrDeleteRequest(url, "GET")

    if (response.type === "success") {
      setProjects(response.data)
    } else {
      setProjects([])
    }
  }

  const getCompanies = async () => {
    const companyResponse = await getOrDeleteRequest("company/filter", "GET")
    if (companyResponse.type === "success") {
      setCompanies(
        companyResponse.data.length > 0
          ? companyResponse.data.map((item) => {
              return {
                id: item.id,
                name: `${item.company_name} ${item.trade_name ? `(${item.trade_name})` : ""}`,
              }
            })
          : [],
      )
    }
  }

  const validateProjectSelection = (projectIds) => {
    if (selectedCompany) {
      if (projectIds.length === 0) {
        setValidationError("Debe seleccionar al menos 1 proyecto cuando hay una empresa seleccionada")
        return false
      }
    } else {
      if (projectIds.length < 2) {
        setValidationError("Debe seleccionar al menos 2 proyectos cuando no hay empresa seleccionada")
        return false
      }
    }

    setValidationError("")
    return true
  }

  const getProjectsSelected = async (projects) => {
    if (!validateProjectSelection(projects)) {
      setSelectedProjects(projects)
      setChartData([])
      setCommonIndicators([])
      setMockData({})
      return
    }

    setLoading(true)
    setValidationError("")

    const response = await postOrPutRequest(`dashboard/data-projects`, "POST", {
      project_ids: projects,
      company_id: selectedCompany || null,
    })

    if (response.type === "success" && response.data) {
      const transformedData = {}

      Object.entries(response.data).forEach(([projectKey, projectData]) => {
        const projectId = projectKey.split("_")[0]
        transformedData[projectId] = {}

        Object.entries(projectData).forEach(([dateKey, indicators]) => {
          transformedData[projectId][dateKey] = {}

          Object.entries(indicators).forEach(([indicatorKey, indicatorValue]) => {
            const indicatorId = indicatorKey.split("_")[0]
            transformedData[projectId][dateKey][indicatorId] = indicatorValue
          })
        })
      })

      setMockData(transformedData)
      setSelectedProjects(projects)
      setSelectedProjectForDetail(projects[0])

      const periodsList = Object.values(transformedData).map((project) => Object.keys(project))

      const commonPeriods = periodsList.reduce((a, b) => a.filter((period) => b.includes(period)))

      const sortedPeriods = commonPeriods.sort()

      setAvailablePeriods(sortedPeriods)
      if (sortedPeriods.length > 0) {
        setSelectedPeriod(sortedPeriods[0])
        setStartPeriod(sortedPeriods[0])
        setEndPeriod(sortedPeriods[sortedPeriods.length - 1])
      }
    } else {
      setSelectedProjects(projects)
      setMockData({})
      setChartData([])
      setCommonIndicators([])
    }

    setLoading(false)
  }

  const handleCompanyChange = (companyId) => {
    setSelectedCompany(companyId)
    setSelectedProjects([])
    setValidationError("")
    setMockData({})
    setChartData([])
    setCommonIndicators([])
    setAvailablePeriods([])
    setSelectedPeriod("")
    setStartPeriod("")
    setEndPeriod("")
    setPeriodType("specific")

    setSelectedProjectForDetail("")
    setSelectedIndicatorForHistory("")
    setHistoryChartData([])
    setSelectedIndicatorTerms([])
    setSelectedIndicatorForTemporal("")
    getProjects(companyId)
  }

  const handleProjectsChange = (selectedProjectIds) => {
    setSelectedIndicatorForHistory("")
    setHistoryChartData([])
    setSelectedIndicatorTerms([])
    setSelectedIndicatorForTemporal("")

    getProjectsSelected(selectedProjectIds)
  }

  const allFunctions = async () => {
    setLoading(true)
    await getCompanies()
    await getProjects()
    setLoading(false)
  }

  useEffect(() => {
    allFunctions()
  }, [])

  useEffect(() => {
    if (selectedProjects.length > 0 && mockData && Object.keys(mockData).length > 0) {
      findCommonIndicators()
    }
  }, [selectedProjects, selectedPeriod, startPeriod, endPeriod, periodType, mockData])

  useEffect(() => {
    if (commonIndicators.length > 0 || (selectedCompany && selectedProjects.length === 1)) {
      prepareChartData()
    }
  }, [
    commonIndicators,
    selectedCompany,
    selectedProjects,
    periodType,
    selectedPeriod,
    startPeriod,
    endPeriod,
    selectedIndicatorForTemporal,
  ])

  const getSelectedPeriods = () => {
    switch (periodType) {
      case "specific":
        return selectedPeriod ? [selectedPeriod] : []
      case "range":
        if (!startPeriod || !endPeriod) return []
        const startIndex = availablePeriods.indexOf(startPeriod)
        const endIndex = availablePeriods.indexOf(endPeriod)
        if (startIndex === -1 || endIndex === -1) return []
        return availablePeriods.slice(startIndex, endIndex + 1)
      case "all":
        return availablePeriods
      default:
        return []
    }
  }

  const findCommonIndicators = () => {
    if (!mockData || Object.keys(mockData).length === 0) {
      setCommonIndicators([])
      return
    }

    const periodsToAnalyze = getSelectedPeriods()
    if (periodsToAnalyze.length === 0) {
      setCommonIndicators([])
      return
    }

    if (selectedCompany && selectedProjects.length === 1) {
      const projectId = selectedProjects[0]
      const allIndicators = new Set()

      periodsToAnalyze.forEach((period) => {
        const periodData = mockData[projectId]?.[period]
        if (periodData) {
          Object.keys(periodData).forEach((indicatorKey) => {
            allIndicators.add(indicatorKey)
          })
        }
      })

      if (allIndicators.size === 0) {
        setCommonIndicators([])
        return
      }

      const indicators = Array.from(allIndicators)
        .map((indicatorKey) => {
          const firstPeriodWithData = periodsToAnalyze.find((period) => mockData[projectId]?.[period]?.[indicatorKey])

          if (!firstPeriodWithData) return null

          const indicator = mockData[projectId][firstPeriodWithData][indicatorKey]
          return {
            id: indicatorKey,
            name: indicator.indicator_name,
            indicator_abbv: indicator.indicator_abbv,
            unit: indicator.indicator_unit,
          }
        })
        .filter(Boolean)

      setCommonIndicators(indicators)
      return
    }

    const indicatorsByProjectAndPeriod = selectedProjects.map((projectId) => {
      const projectIndicators = new Set()
      periodsToAnalyze.forEach((period) => {
        if (mockData[projectId] && mockData[projectId][period]) {
          Object.keys(mockData[projectId][period]).forEach((indicatorKey) => {
            projectIndicators.add(indicatorKey)
          })
        }
      })
      return Array.from(projectIndicators)
    })

    if (indicatorsByProjectAndPeriod.length === 0) {
      setCommonIndicators([])
      return
    }

    const commonIndicatorsList = indicatorsByProjectAndPeriod.reduce((a, b) =>
      a.filter((indicator) => b.includes(indicator)),
    )

    const common = commonIndicatorsList
      .map((indicatorId) => {
        let indicatorData = null
        for (const projectId of selectedProjects) {
          for (const period of periodsToAnalyze) {
            if (mockData[projectId]?.[period]?.[indicatorId]) {
              indicatorData = mockData[projectId][period][indicatorId]
              break
            }
          }
          if (indicatorData) break
        }

        if (!indicatorData) return null

        return {
          id: indicatorId,
          name: indicatorData.indicator_name,
          indicator_abbv: indicatorData.indicator_abbv,
          unit: indicatorData.indicator_unit,
        }
      })
      .filter(Boolean)

    setCommonIndicators(common)
  }

  const prepareChartData = () => {
    if (!mockData || Object.keys(mockData).length === 0) {
      setChartData([])
      return
    }

    const periodsToAnalyze = getSelectedPeriods()
    if (periodsToAnalyze.length === 0) {
      setChartData([])
      return
    }

    if (selectedCompany && selectedProjects.length === 1) {
      const projectId = selectedProjects[0]

      if (periodType === "specific") {
        const projectData = mockData[projectId]?.[selectedPeriod]
        if (!projectData) {
          setChartData([])
          return
        }

        const data = Object.entries(projectData).map(([indicatorId, indicatorData]) => {
          const chartItem = {
            id: indicatorId,
            name: indicatorData.indicator_name,
            indicator_abbv: indicatorData.indicator_abbv,
            unit: indicatorData.indicator_unit,
            [projectId]: indicatorData.total_result,
          }

          if (isValidValue(indicatorData.limits?.baseline)) {
            chartItem.baseline = indicatorData.limits.baseline
          }

          if (isValidValue(indicatorData.limits?.target)) {
            chartItem.target = indicatorData.limits.target
          }

          return chartItem
        })

        setChartData(data)
      } else {
        if (!selectedIndicatorForTemporal) {
          setChartData([])
          return
        }

        const data = periodsToAnalyze
          .map((period) => {
            const periodData = mockData[projectId]?.[period]
            if (!periodData) return null

            const chartItem = { period }
            const indicatorData = periodData[selectedIndicatorForTemporal]

            if (indicatorData) {
              chartItem[`${selectedIndicatorForTemporal}_value`] = Number.parseFloat(indicatorData.total_result)
              chartItem[`${selectedIndicatorForTemporal}_name`] = indicatorData.indicator_name
              chartItem[`${selectedIndicatorForTemporal}_unit`] = indicatorData.indicator_unit

              if (isValidValue(indicatorData.limits?.baseline)) {
                chartItem[`${selectedIndicatorForTemporal}_baseline`] = indicatorData.limits.baseline
              }

              if (isValidValue(indicatorData.limits?.target)) {
                chartItem[`${selectedIndicatorForTemporal}_target`] = indicatorData.limits.target
              }
            }

            return chartItem
          })
          .filter(Boolean)

        setChartData(data)
      }
      return
    }

    if (periodType === "specific") {
      const data = commonIndicators.map((indicator) => {
        const chartItem = {
          name: indicator.name,
          indicator_abbv: indicator.indicator_abbv,
          id: indicator.id,
          unit: indicator.unit,
        }

        selectedProjects.forEach((projectId) => {
          const indicatorData = mockData[projectId]?.[selectedPeriod]?.[indicator.id]
          if (indicatorData) {
            chartItem[projectId] = Number.parseFloat(indicatorData.total_result)
            if (indicatorData.limits) {
              if (isValidValue(indicatorData.limits.baseline)) {
                chartItem[`${projectId}_baseline`] = indicatorData.limits.baseline
              }
              if (isValidValue(indicatorData.limits.target)) {
                chartItem[`${projectId}_target`] = indicatorData.limits.target
              }
            }
          }
        })

        return chartItem
      })

      setChartData(data)
    } else {
      if (!selectedIndicatorForTemporal) {
        setChartData([])
        return
      }

      const data = periodsToAnalyze.map((period) => {
        const chartItem = { period }

        selectedProjects.forEach((projectId) => {
          const indicatorData = mockData[projectId]?.[period]?.[selectedIndicatorForTemporal]
          if (indicatorData) {
            chartItem[`${projectId}_${selectedIndicatorForTemporal}`] = Number.parseFloat(indicatorData.total_result)
          }
        })

        return chartItem
      })

      setChartData(data)
    }
  }

  const prepareHistoryChartData = (projectId, indicatorId) => {
    // console.log("prepareHistoryChartData called with:", { projectId, indicatorId })

    if (!projectId || !indicatorId || !mockData[projectId]) {
      setHistoryChartData([])
      setSelectedIndicatorTerms([])
      return
    }

    const indicatorExistsInPeriods = availablePeriods.some((period) => {
      const exists = mockData[projectId][period]?.[indicatorId]
      return exists
    })

    if (!indicatorExistsInPeriods) {
      setHistoryChartData([])
      setSelectedIndicatorTerms([])
      return
    }

    const firstPeriodWithData = availablePeriods.find((period) => mockData[projectId][period]?.[indicatorId])

    if (firstPeriodWithData) {
      const indicatorData = mockData[projectId][firstPeriodWithData][indicatorId]
      setSelectedIndicatorTerms(indicatorData.terms || [])
    } else {
      setSelectedIndicatorTerms([])
    }

    const historyData = availablePeriods
      .map((period) => {
        const indicatorData = mockData[projectId][period]?.[indicatorId]
        if (!indicatorData) return null

        const termsData = {}
        if (indicatorData.terms) {
          indicatorData.terms.forEach((term) => {
            termsData[`term_${term.term_name}`] = term.value
          })
        }

        const dataPoint = {
          period,
          value: Number.parseFloat(indicatorData.total_result),
          indicatorName: indicatorData.indicator_name,
          indicatorAbbv: indicatorData.indicator_abbv,
          unit: indicatorData.indicator_unit,
          ...termsData,
        }

        if (isValidValue(indicatorData.limits?.baseline)) {
          dataPoint.baseline = indicatorData.limits.baseline
        }

        if (isValidValue(indicatorData.limits?.target)) {
          dataPoint.target = indicatorData.limits.target
        }

        return dataPoint
      })
      .filter((item) => item !== null)

    setHistoryChartData(historyData)
  }

  const renderPeriodSelector = () => {
    if (availablePeriods.length === 0) return null

    return (
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Período</label>
          <select
            className="w-full p-2 border border-gray-300 rounded-md"
            value={periodType}
            onChange={(e) => {
              setPeriodType(e.target.value)
              setSelectedIndicatorForHistory("")
              setHistoryChartData([])
              setSelectedIndicatorTerms([])
              setSelectedIndicatorForTemporal("")
            }}
          >
            <option value="specific">Fecha específica</option>
            <option value="range">Rango de fechas</option>
            <option value="all">Todas las fechas</option>
          </select>
        </div>

        {periodType === "specific" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Período</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
            >
              {availablePeriods.map((period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </select>
          </div>
        )}

        {periodType === "range" && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha inicial</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md"
                value={startPeriod}
                onChange={(e) => setStartPeriod(e.target.value)}
              >
                {availablePeriods.map((period) => (
                  <option key={period} value={period}>
                    {period}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha final</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md"
                value={endPeriod}
                onChange={(e) => setEndPeriod(e.target.value)}
              >
                {availablePeriods.map((period) => (
                  <option key={period} value={period}>
                    {period}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {periodType === "all" && (
          <div className="bg-blue-50 p-3 rounded-md">
            <p className="text-sm text-blue-700">
              Se mostrarán datos de todos los períodos disponibles: {availablePeriods.join(", ")}
            </p>
          </div>
        )}
      </div>
    )
  }

  const renderTermsInChart = () => {
    if (!selectedIndicatorForHistory || selectedIndicatorTerms.length === 0) {
      return null
    }

    return selectedIndicatorTerms.map((term, index) => {
      const colors = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"]
      return (
        <Bar
          key={`term_${term.term_name}`}
          dataKey={`term_${term.term_name}`}
          name={`${term.term_name} (${term.unit})`}
          fill={colors[index % colors.length]}
          barSize={20}
        />
      )
    })
  }

  const renderProjectHistoryChart = () => {
    if (!selectedProjectForDetail || !selectedIndicatorForHistory) {
      return (
        <div className="bg-gray-50 p-8 rounded-lg text-center">
          <p className="text-gray-500">Seleccione un proyecto y un indicador para ver su histórico</p>
        </div>
      )
    }

    if (historyChartData.length === 0) {
      return (
        <div className="bg-gray-50 p-8 rounded-lg text-center">
          <p className="text-gray-500">
            No hay datos históricos disponibles para esta combinación de proyecto e indicador
          </p>
        </div>
      )
    }

    const project = projects.find((p) => p.id === selectedProjectForDetail)
    const currentIndicator = historyChartData[0]

    const hasBaseline = historyChartData.some((item) => item.baseline !== undefined)
    const hasTarget = historyChartData.some((item) => item.target !== undefined)

    return (
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="text-lg font-medium mb-2">{currentIndicator.indicatorName}</h3>
        <p className="text-sm text-gray-500 mb-4">
          Histórico para {project?.name} - {currentIndicator.indicatorAbbv} ({currentIndicator.unit})
        </p>

        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              key={`history-${selectedProjectForDetail}-${selectedIndicatorForHistory}-${historyChartData.length}`}
              data={historyChartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="period" angle={-45} textAnchor="end" height={70} tick={{ fontSize: 12 }} />
              <YAxis />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
                formatter={(value, name) => {
                  if (name === "value") return [value, `Valor actual (${currentIndicator.unit})`]
                  if (name === "baseline") return [value, `Línea base (${currentIndicator.unit})`]
                  if (name === "target") return [value, `Meta (${currentIndicator.unit})`]
                  if (name.startsWith("term_")) {
                    const termName = name.replace("term_", "")
                    const term = selectedIndicatorTerms.find((t) => t.term_name === termName)
                    return [value, `${termName} (${term?.unit || ""})`]
                  }
                  return [value, name]
                }}
                labelFormatter={(label) => `Periodo: ${label}`}
              />
              <Legend />

              <Line
                type="monotone"
                dataKey="value"
                name={`Valor actual (${currentIndicator.unit})`}
                stroke="#8884d8"
                strokeWidth={3}
                dot={{ r: 6 }}
                activeDot={{ r: 8 }}
              />

              {hasBaseline && (
                <Line
                  type="monotone"
                  dataKey="baseline"
                  name={`Línea base (${currentIndicator.unit})`}
                  stroke="#d46b08"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              )}

              {hasTarget && (
                <Line
                  type="monotone"
                  dataKey="target"
                  name={`Meta (${currentIndicator.unit})`}
                  stroke="#389e0d"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              )}

              {renderTermsInChart()}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-6">
          <h4 className="text-md font-medium mb-2">Términos del indicador</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selectedIndicatorTerms.map((term, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">{term.term_name}</p>
                <p className="text-sm text-gray-600">{term.term_detail}</p>
                <p className="text-sm">
                  <span className="font-medium">Valor:</span> {term.value} {term.unit}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const renderProjectDetailTab = () => {
    if (!selectedProjectForDetail) return null

    const project = projects.find((p) => p.id === selectedProjectForDetail)
    if (!project) return null

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Proyecto</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md"
              value={selectedProjectForDetail}
              onChange={(e) => {
                setSelectedProjectForDetail(e.target.value)
                setSelectedIndicatorForHistory("")
                setHistoryChartData([])
                setSelectedIndicatorTerms([])
              }}
            >
              {selectedProjects.map((projectId) => {
                const proj = projects.find((p) => p.id === projectId)
                return (
                  <option key={projectId} value={projectId}>
                    {proj?.name || projectId}
                  </option>
                )
              })}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Indicador</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md"
              value={selectedIndicatorForHistory}
              onChange={(e) => {
                setSelectedIndicatorForHistory(e.target.value)
                prepareHistoryChartData(selectedProjectForDetail, e.target.value)
              }}
              disabled={!selectedProjectForDetail}
            >
              <option value="">Seleccione un indicador</option>
              {commonIndicators.map((indicator) => (
                <option key={indicator.id} value={indicator.id}>
                  {indicator.indicator_abbv} - {indicator.name} ({indicator.unit})
                </option>
              ))}
            </select>
          </div>
        </div>

        {renderProjectHistoryChart()}
      </div>
    )
  }

  const renderComparisonTab = () => {
    const periodsToAnalyze = getSelectedPeriods()

    if (selectedCompany && selectedProjects.length === 1) {
      const projectId = selectedProjects[0]
      const project = projects.find((p) => p.id === projectId)

      if (periodType === "specific") {
        return (
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4 text-gray-700">
              Indicadores para {project?.name || projectId} - Periodo: {selectedPeriod}
            </h2>

            <div className="h-96 mb-8">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  key={`single-${selectedCompany?.id}-${selectedProjects.join("-")}-${selectedPeriod}`}
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="indicator_abbv" angle={-45} textAnchor="end" height={70} tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip
                    formatter={(value) => {
                      const indicator = chartData.find((item) => item[projectId] === value)
                      return [value, indicator?.unit ? `(${indicator.unit})` : ""]
                    }}
                  />
                  <Legend />
                  <Bar dataKey={projectId} name={`${project?.name || projectId}`} fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-8">
              <h3 className="text-lg font-medium mb-3 text-gray-700">Datos detallados</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Indicador
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Valor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Línea Base
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Meta
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {chartData.map((row, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>{row.name}</div>
                          <div className="text-xs text-gray-500">
                            {row.indicator_abbv} ({row.unit})
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row[projectId] ?? "N/A"}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {row.baseline !== undefined ? row.baseline : "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {row.target !== undefined ? row.target : "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )
      } else {
        return (
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4 text-gray-700">
              {project?.name || projectId}
              {periodType === "range" && ` (${startPeriod} - ${endPeriod})`}
              {periodType === "all" && " (Todos los períodos)"}
            </h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seleccione un indicador para visualizar
              </label>
              <select
                className="w-full max-w-md p-2 border border-gray-300 rounded-md"
                value={selectedIndicatorForTemporal}
                onChange={(e) => setSelectedIndicatorForTemporal(e.target.value)}
              >
                <option value="">Seleccione un indicador</option>
                {commonIndicators.map((indicator) => (
                  <option key={indicator.id} value={indicator.id}>
                    {indicator.indicator_abbv} - {indicator.name} ({indicator.unit})
                  </option>
                ))}
              </select>
            </div>

            {selectedIndicatorForTemporal ? (
              (() => {
                const selectedIndicator = commonIndicators.find((ind) => ind.id === selectedIndicatorForTemporal)
                if (!selectedIndicator) return null

                return (
                  <div>
                    <h3 className="text-lg font-medium mb-4">
                      {selectedIndicator.name} ({selectedIndicator.indicator_abbv}) - {selectedIndicator.unit}
                    </h3>

                    <div className="h-96 mb-8">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          key={`temporal-single-${projectId}-${selectedIndicatorForTemporal}-${periodType}-${periodsToAnalyze.length}`}
                          data={chartData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="period" angle={-45} textAnchor="end" height={70} tick={{ fontSize: 12 }} />
                          <YAxis />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#fff",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                            formatter={(value, name) => {
                              if (name === `${selectedIndicatorForTemporal}_value`)
                                return [value, `Valor (${selectedIndicator.unit})`]
                              if (name === `${selectedIndicatorForTemporal}_baseline`)
                                return [value, `Línea base (${selectedIndicator.unit})`]
                              if (name === `${selectedIndicatorForTemporal}_target`)
                                return [value, `Meta (${selectedIndicator.unit})`]
                              return [value, name]
                            }}
                            labelFormatter={(label) => `Período: ${label}`}
                          />
                          <Legend />

                          <Line
                            type="monotone"
                            dataKey={`${selectedIndicatorForTemporal}_value`}
                            name={`Valor (${selectedIndicator.unit})`}
                            stroke="#8884d8"
                            strokeWidth={3}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                          />

                          {chartData.some((item) => item[`${selectedIndicatorForTemporal}_baseline`] !== undefined) && (
                            <Line
                              type="monotone"
                              dataKey={`${selectedIndicatorForTemporal}_baseline`}
                              name={`Línea base (${selectedIndicator.unit})`}
                              stroke="#d46b08"
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              dot={false}
                            />
                          )}

                          {chartData.some((item) => item[`${selectedIndicatorForTemporal}_target`] !== undefined) && (
                            <Line
                              type="monotone"
                              dataKey={`${selectedIndicatorForTemporal}_target`}
                              name={`Meta (${selectedIndicator.unit})`}
                              stroke="#389e0d"
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              dot={false}
                            />
                          )}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Tabla de datos detallados */}
                    <div className="mt-6">
                      <h4 className="text-lg font-medium mb-3 text-gray-700">
                        Datos detallados - {selectedIndicator.name}
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Período
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Valor
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Línea Base
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Meta
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {chartData.map((row, idx) => (
                              <tr key={idx}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {row.period}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {row[`${selectedIndicatorForTemporal}_value`] !== undefined
                                    ? row[`${selectedIndicatorForTemporal}_value`]
                                    : "N/A"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {row[`${selectedIndicatorForTemporal}_baseline`] !== undefined
                                    ? row[`${selectedIndicatorForTemporal}_baseline`]
                                    : "N/A"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {row[`${selectedIndicatorForTemporal}_target`] !== undefined
                                    ? row[`${selectedIndicatorForTemporal}_target`]
                                    : "N/A"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )
              })()
            ) : (
              <div className="bg-gray-50 p-8 rounded-lg text-center">
                <p className="text-gray-500">Seleccione un indicador para visualizar la evolución temporal</p>
              </div>
            )}
          </div>
        )
      }
    }

    if (periodType === "specific") {
      return (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">Comparación para el periodo: {selectedPeriod}</h2>

          <div className="h-96">
            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                key={`comparison-${selectedProjects.join("-")}-${selectedPeriod}-${chartData.length}`}
                data={chartData}
                margin={{ top: 20, right: 20, left: 10, bottom: 50 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="indicator_abbv"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 12 }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {selectedProjects.map((projectId, index) => {
                  const project = projects.find((p) => p.id === projectId)
                  const colors = ["#8884d8", "#82ca9d", "#ffc658", "#ff7f50", "#6a5acd"]

                  return (
                    <Line
                      key={projectId}
                      type="monotone"
                      dataKey={projectId}
                      stroke={colors[index % colors.length]}
                      strokeWidth={2}
                      name={project?.name || projectId}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  )
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-medium mb-3 text-gray-700">Datos detallados</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Indicador
                    </th>
                    {selectedProjects.map((projectId) => {
                      const project = projects.find((p) => p.id === projectId)
                      return (
                        <th
                          key={projectId}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {project?.name || projectId}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {chartData.map((row, idx) => (
                    <tr key={idx}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>{row.name}</div>
                        <div className="text-xs text-gray-500">
                          {row.indicator_abbv} ({row.unit})
                        </div>
                      </td>
                      {selectedProjects.map((projectId) => (
                        <td key={projectId} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {row[projectId] !== null ? row[projectId] : "N/A"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )
    } else {
      return (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">
            Evolución temporal comparativa
            {periodType === "range" && ` (${startPeriod} - ${endPeriod})`}
            {periodType === "all" && " (Todos los períodos)"}
          </h2>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seleccione un indicador para visualizar
            </label>
            <select
              className="w-full max-w-md p-2 border border-gray-300 rounded-md"
              value={selectedIndicatorForTemporal}
              onChange={(e) => setSelectedIndicatorForTemporal(e.target.value)}
            >
              <option value="">Seleccione un indicador</option>
              {commonIndicators.map((indicator) => (
                <option key={indicator.id} value={indicator.id}>
                  {indicator.indicator_abbv} - {indicator.name} ({indicator.unit})
                </option>
              ))}
            </select>
          </div>

          {selectedIndicatorForTemporal ? (
            (() => {
              const selectedIndicator = commonIndicators.find((ind) => ind.id === selectedIndicatorForTemporal)
              if (!selectedIndicator) return null

              return (
                <div>
                  <h3 className="text-lg font-medium mb-4">
                    {selectedIndicator.name} ({selectedIndicator.indicator_abbv}) - {selectedIndicator.unit}
                  </h3>

                  <div className="h-96 mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        key={`temporal-multi-${selectedIndicator.id}-${periodType}-${periodsToAnalyze.length}`}
                        data={chartData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" angle={-45} textAnchor="end" height={70} tick={{ fontSize: 12 }} />
                        <YAxis />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#fff",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          }}
                          formatter={(value, name) => {
                            const projectId = name.replace(`_${selectedIndicator.id}`, "")
                            const project = projects.find((p) => p.id === projectId)
                            return [value, `${project?.name || projectId} (${selectedIndicator.unit})`]
                          }}
                          labelFormatter={(label) => `Período: ${label}`}
                        />
                        <Legend />

                        {selectedProjects.map((projectId, projectIndex) => {
                          const project = projects.find((p) => p.id === projectId)
                          const colors = [
                            "#8884d8",
                            "#82ca9d",
                            "#ffc658",
                            "#ff7f50",
                            "#6a5acd",
                            "#ff69b4",
                            "#00ced1",
                            "#32cd32",
                          ]

                          return (
                            <Line
                              key={`${projectId}_${selectedIndicator.id}`}
                              type="monotone"
                              dataKey={`${projectId}_${selectedIndicator.id}`}
                              stroke={colors[projectIndex % colors.length]}
                              strokeWidth={3}
                              name={`${projectId}_${selectedIndicator.id}`}
                              dot={{ r: 4, fill: colors[projectIndex % colors.length] }}
                              activeDot={{ r: 6, fill: colors[projectIndex % colors.length] }}
                            />
                          )
                        })}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="mt-6">
                    <h4 className="text-lg font-medium mb-3 text-gray-700">
                      Datos detallados - {selectedIndicator.name}
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Período
                            </th>
                            {selectedProjects.map((projectId) => {
                              const project = projects.find((p) => p.id === projectId)
                              return (
                                <th
                                  key={projectId}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  {project?.name || projectId}
                                </th>
                              )
                            })}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {chartData.map((row, idx) => (
                            <tr key={idx}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {row.period}
                              </td>
                              {selectedProjects.map((projectId) => (
                                <td key={projectId} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {row[`${projectId}_${selectedIndicator.id}`] !== undefined
                                    ? row[`${projectId}_${selectedIndicator.id}`]
                                    : "N/A"}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {selectedProjects.map((projectId) => {
                      const project = projects.find((p) => p.id === projectId)
                      const values = chartData
                        .map((row) => row[`${projectId}_${selectedIndicator.id}`])
                        .filter((val) => val !== undefined && val !== null)
                        .map((val) => Number.parseFloat(val))

                      if (values.length === 0) return null

                      const avg = values.reduce((a, b) => a + b, 0) / values.length
                      const min = Math.min(...values)
                      const max = Math.max(...values)
                      const trend = values.length > 1 ? ((values[values.length - 1] - values[0]) / values[0]) * 100 : 0

                      return (
                        <div key={projectId} className="bg-gray-50 p-4 rounded-lg">
                          <h5 className="font-medium text-gray-900 mb-2">{project?.name || projectId}</h5>
                          <div className="space-y-1 text-sm">
                            <div>
                              Promedio: <span className="font-medium">{avg.toFixed(2)}</span>
                            </div>
                            <div>
                              Mínimo: <span className="font-medium">{min.toFixed(2)}</span>
                            </div>
                            <div>
                              Máximo: <span className="font-medium">{max.toFixed(2)}</span>
                            </div>
                            <div>
                              Tendencia:
                              <span className={`font-medium ml-1 ${trend >= 0 ? "text-green-600" : "text-red-600"}`}>
                                {trend >= 0 ? "+" : ""}
                                {trend.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div> */}
                </div>
              )
            })()
          ) : (
            <div className="bg-gray-50 p-8 rounded-lg text-center">
              <p className="text-gray-500">Seleccione un indicador para visualizar la evolución temporal comparativa</p>
            </div>
          )}
        </div>
      )
    }
  }

  const renderTabs = () => (
    <div className="mb-6 border-b border-gray-200">
      <nav className="-mb-px flex space-x-8">
        <button
          onClick={() => setActiveTab("comparison")}
          className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "comparison" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}
        >
          Comparación de indicadores
        </button>
        <button
          onClick={() => setActiveTab("details")}
          className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "details" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}
        >
          Análisis de indicadores
        </button>
      </nav>
    </div>
  )

  const canShowComparison = () => {
    const periodsToAnalyze = getSelectedPeriods()

    if (selectedCompany) {
      return selectedProjects.length > 0 && periodsToAnalyze.length > 0 && commonIndicators.length > 0
    } else {
      return selectedProjects.length >= 2 && periodsToAnalyze.length > 0 && commonIndicators.length > 0
    }
  }

  return (
    <MainThemeLayout>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Comparación de Indicadores de Proyectos 
          {selectedCompany && ` - Empresa: ${companies.find(c => c.id === selectedCompany)?.name || selectedCompany}`}
        </h1>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <SelectMultiple
                  name="companies"
                  loading={loading}
                  array={companies}
                  label="Empresas"
                  isMulti={false}
                  value={selectedCompany}
                  onChange={(e) => handleCompanyChange(e)}
                  placeholder="Seleccione una empresa (opcional)"
                />
              </div>

              <div className="bg-white p-4 rounded-lg shadow">
                <SelectMultiple
                  name="projects"
                  loading={loading}
                  array={projects}
                  label="Proyectos"
                  isMulti={true}
                  value={selectedProjects}
                  onChange={(e) => handleProjectsChange(e)}
                  placeholder={selectedCompany ? "Seleccione 1 o más proyectos" : "Seleccione al menos 2 proyectos"}
                />
                {validationError && (
                  <p className="text-sm text-red-500 mt-2 bg-red-50 p-2 rounded">{validationError}</p>
                )}
              </div>

              <div className="md:col-span-2 lg:col-span-1">{renderPeriodSelector()}</div>
            </div>

            {canShowComparison() ? (
              <>
                {renderTabs()}
                {activeTab === "comparison" && renderComparisonTab()}
                {activeTab === "details" && (
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h2 className="text-xl font-semibold mb-4 text-gray-700">Análisis histórico por proyecto</h2>
                    {renderProjectDetailTab()}
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white p-6 rounded-lg shadow text-center">
                <p className="text-gray-600">
                  {validationError ||
                    (selectedCompany
                      ? selectedProjects.length === 0
                        ? "Seleccione al menos 1 proyecto para continuar"
                        : getSelectedPeriods().length === 0
                          ? "Configure el período de comparación"
                          : commonIndicators.length === 0
                            ? "Cargando datos del proyecto..."
                            : "No hay datos disponibles para los proyectos seleccionados"
                      : selectedProjects.length < 2
                        ? "Seleccione al menos 2 proyectos para comparar"
                        : getSelectedPeriods().length === 0
                          ? "Configure el período de comparación"
                          : "No hay indicadores comunes entre los proyectos seleccionados para este periodo.")}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </MainThemeLayout>
  )
}

export default CompareProyects
